import {
  CheckCircleTwoTone,
  ClockCircleOutlined,
  CloseCircleTwoTone,
  SyncOutlined,
} from "@ant-design/icons";
import { assertNever } from "@p0security/frontend-core";
import {
  ErrorDisplay,
  FirestoreDoc,
  useFirestoreCollection,
} from "@p0security/frontend-core/react";
import {
  AssessmentJob,
  AssessmentProgress,
  AssessmentProgressStatus,
  AssessmentProgressTypeDescription,
  AssessmentProgressTypes,
} from "@p0security/shared/types/assessment/job";
import { Flex, List, Progress, Spacing } from "@p0security/ui";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { AuthzButton } from "components/common/AuthzButton";
import { useFlags } from "launchdarkly-react-client-sdk";
import { round, sortBy } from "lodash";
import * as React from "react";
import { useCallback, useContext, useMemo } from "react";
import { colors } from "styles/variables";

import { Tenant } from "../../Login";
import { StyledListInfo } from "../styles";
import { TargetsList } from "./Targets";

type Props = {
  id: string;
  job: AssessmentJob;
};

type AssessmentSummaryProps = {
  showRun: boolean;
};

const completedCheckCircle = <CheckCircleTwoTone twoToneColor="#52c41a" />;
const erroredCloseCircle = (
  <CloseCircleTwoTone twoToneColor={colors.themeColors.red} />
);

export const statusToIcon = (status: AssessmentProgressStatus) => {
  switch (status) {
    case "COMPLETED":
      return completedCheckCircle;
    case "ERRORED":
      return erroredCloseCircle;
    case "IN_PROGRESS":
      return <ClockCircleOutlined />;
    case "NOT_STARTED":
      return <ClockCircleOutlined />;
    default:
      assertNever(status);
  }
};

export const JobSummary: React.FC<Props> = ({ id, job }) => {
  const tenantId = useContext(Tenant);
  const flags = useFlags();
  const { docs: auditProgressDocs } =
    useFirestoreCollection<AssessmentProgress>(
      `o/${tenantId}/job-state/${id}/progress`,
      { live: true }
    );
  const orderedProgress = useMemo(
    () =>
      sortBy(auditProgressDocs ?? [], (a) =>
        AssessmentProgressTypes.indexOf(a.data.name)
      ).filter((a) => flags.assessmentManage || a.id !== "MANAGEMENT"),
    [auditProgressDocs, flags.assessmentManage]
  );

  const renderListItem = useCallback(
    (item: FirestoreDoc<AssessmentProgress>) => {
      const element = (
        <Flex align="center" justify="flex-start">
          {item.data.state === "COMPLETED" ? (
            completedCheckCircle
          ) : job.status === "ERRORED" ? (
            erroredCloseCircle
          ) : item.data.state === "NOT_STARTED" ? (
            <ClockCircleOutlined />
          ) : (
            <SyncOutlined spin />
          )}
          <StyledListInfo>
            {item.data.state !== "COMPLETED" &&
            item.data.state !== "NOT_STARTED" ? (
              <>
                <div style={{ flex: "1" }}>
                  {
                    AssessmentProgressTypeDescription[item.data.name]
                      ?.inProgress
                  }
                </div>
                <Spacing asChild ml="5px">
                  <div style={{ flex: "1" }}>
                    <Progress
                      percent={round(item.data.progress * 100)}
                      status="active"
                      trailColor="#ffffff"
                    />
                    <div>{item.data.message}</div>
                  </div>
                </Spacing>
              </>
            ) : (
              <div>
                {
                  AssessmentProgressTypeDescription[item.data.name]?.[
                    item.data.state === "COMPLETED" ? "completed" : "inProgress"
                  ]
                }
              </div>
            )}
          </StyledListInfo>
        </Flex>
      );
      return (
        <List.Item>
          <List.Item.Meta title={element} />
        </List.Item>
      );
    },
    [job]
  );

  return (
    <>
      <Spacing mb={10}>
        <List<FirestoreDoc<AssessmentProgress>>
          className="[&_.ant-list-item]:p-0! [&_.ant-list-item-meta-title]:mb-0!"
          itemLayout="horizontal"
          size="small"
          split={false}
          loading={!auditProgressDocs}
          dataSource={orderedProgress}
          renderItem={renderListItem}
        />
      </Spacing>
      {job.status === "ERRORED" && <ErrorDisplay error={job.error?.message} />}
    </>
  );
};

export const AssessmentSummary: React.FC<AssessmentSummaryProps> = (props) =>
  //TODO: Pass in current, runAssessmentNow, and assessment instead of using useContext
  {
    const { current, runAssessmentNow, assessment } = useContext(
      SelectedEnvironmentContext
    );
    const { showRun } = props;
    const currentJobDoc = current.doc?.data;
    const currentAssessmentDoc = assessment.doc;
    return (
      <Flex gap="0.5em">
        <Flex className="w-[70%]" vertical>
          {currentJobDoc && (
            <JobSummary job={currentJobDoc} id={currentJobDoc.jobId} />
          )}
          {showRun && (
            <AuthzButton
              roles={["owner", "iamOwner"]}
              type="primary"
              disabledProps={!current.isCompleted}
              onClick={runAssessmentNow}
              /* eslint-disable-line react/forbid-component-props */ style={{
                maxWidth: 300,
              }}
            >
              Run unscheduled assessment now
            </AuthzButton>
          )}
        </Flex>
        <div style={{ width: "30%" }}>
          <TargetsList targets={currentAssessmentDoc?.data.targets ?? []} />
        </div>
      </Flex>
    );
  };
